import { createI18n } from 'vue-i18n';
import { localeFromPath } from '../seoConfig';
import { russianPluralRule } from './helpers';

export const DEFAULT_LOCALE = 'en';
export const FALLBACK_LOCALE = 'en';
export const LOCALE_STORAGE_KEY = 'app-locale';
export const LOCALE_CHANGED_EVENT = 'app-locale-changed';
export const SUPPORTED_LOCALES = ['en', 'ru'];

const PRIMEVUE_LOCALES = {
  en: {
    startsWith: 'Starts with',
    contains: 'Contains',
    notContains: 'Does not contain',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Does not equal',
    noFilter: 'No filter',
    lt: 'Less than',
    lte: 'Less than or equal to',
    gt: 'Greater than',
    gte: 'Greater than or equal to',
    dateIs: 'Date is',
    dateIsNot: 'Date is not',
    dateBefore: 'Date is before',
    dateAfter: 'Date is after',
    clear: 'Clear',
    apply: 'Apply',
    matchAll: 'Match all',
    matchAny: 'Match any',
    addRule: 'Add rule',
    removeRule: 'Remove rule',
    accept: 'Yes',
    reject: 'No',
    choose: 'Choose',
    upload: 'Upload',
    cancel: 'Cancel',
    emptyMessage: 'No results found',
    searchMessage: '{0} results are available',
    selectionMessage: '{0} items selected',
    emptySelectionMessage: 'No selected item',
    emptySearchMessage: 'No results found',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    passwordPrompt: 'Enter a password',
    aria: {
      close: 'Close',
      listLabel: 'Option list',
    },
  },
  ru: {
    startsWith: 'Начинается с',
    contains: 'Содержит',
    notContains: 'Не содержит',
    endsWith: 'Заканчивается на',
    equals: 'Равно',
    notEquals: 'Не равно',
    noFilter: 'Без фильтра',
    lt: 'Меньше',
    lte: 'Меньше или равно',
    gt: 'Больше',
    gte: 'Больше или равно',
    dateIs: 'Дата равна',
    dateIsNot: 'Дата не равна',
    dateBefore: 'Дата до',
    dateAfter: 'Дата после',
    clear: 'Очистить',
    apply: 'Применить',
    matchAll: 'Все условия',
    matchAny: 'Любое условие',
    addRule: 'Добавить правило',
    removeRule: 'Удалить правило',
    accept: 'Да',
    reject: 'Нет',
    choose: 'Выбрать',
    upload: 'Загрузить',
    cancel: 'Отмена',
    emptyMessage: 'Ничего не найдено',
    searchMessage: 'Доступно результатов: {0}',
    selectionMessage: 'Выбрано элементов: {0}',
    emptySelectionMessage: 'Ничего не выбрано',
    emptySearchMessage: 'Ничего не найдено',
    weak: 'Слабый',
    medium: 'Средний',
    strong: 'Надёжный',
    passwordPrompt: 'Введите пароль',
    aria: {
      close: 'Закрыть',
      listLabel: 'Список вариантов',
    },
  },
};

function normalizeLocale(locale) {
  const language = String(locale || '').trim().toLowerCase().split('-')[0];
  return SUPPORTED_LOCALES.includes(language) ? language : null;
}

export function resolveInitialLocale() {
  return typeof window === 'undefined' ? DEFAULT_LOCALE : localeFromPath(window.location.pathname);
}

const initialLocale = resolveInitialLocale();

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: initialLocale,
  fallbackLocale: false,
  messages: {},
  pluralRules: {
    ru: russianPluralRule,
  },
});

export function getLocale() {
  return i18n.global.locale.value;
}

const localeLoaders = {
  en: () => import('./locales/en'),
  ru: () => import('./locales/ru'),
};
const pendingLocales = new Map();
let localeRequest = 0;

export async function loadLocaleMessages(locale) {
  if (i18n.global.availableLocales.includes(locale)) return;
  if (!pendingLocales.has(locale)) {
    pendingLocales.set(locale, localeLoaders[locale]().then(({ default: messages }) => {
      i18n.global.setLocaleMessage(locale, messages);
    }).finally(() => pendingLocales.delete(locale)));
  }
  await pendingLocales.get(locale);
}

export async function setLocale(locale, { persist = true } = {}) {
  const nextLocale = normalizeLocale(locale) || DEFAULT_LOCALE;
  const request = ++localeRequest;
  await loadLocaleMessages(nextLocale);
  if (request !== localeRequest) return getLocale();
  i18n.global.locale.value = nextLocale;
  document.documentElement.lang = nextLocale;

  if (persist) {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    } catch {
      // The selected language still applies for the current session.
    }
  }

  window.dispatchEvent(new CustomEvent(LOCALE_CHANGED_EVENT, {
    detail: { locale: nextLocale },
  }));

  return nextLocale;
}

export function initializeLocale() {
  return setLocale(initialLocale, { persist: false });
}

export function getPrimeVueLocale(locale = getLocale()) {
  return PRIMEVUE_LOCALES[normalizeLocale(locale) || DEFAULT_LOCALE];
}

export function translate(key, ...args) {
  return i18n.global.t(key, ...args);
}
